// pages/MarketMoney.jsx
// DWI Market Phase 6 — Admin → Market: the Dashboard (everything to act on
// today, money in, what's owed), Seller payables (RECORDS ONLY — you pay
// sellers outside the app and mark them paid here; the app moves no money)
// and the Margin report (revenue, cost, margin, delivery fee vs trip cost).
import { useState, useEffect, useCallback } from 'react';
import toast from 'react-hot-toast';
import { marketAPI } from '../services/api';
import { Button, Card, Input, Select, Badge, Modal, Table, Td, Empty, Spinner, SectionHeader } from '../components/ui';

const ugx = n => `UGX ${Number(n || 0).toLocaleString('en-US')}`;
const day = d => (d ? new Date(d).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }) : '');
const isoDay = d => new Date(d).toISOString().slice(0, 10);
const wa = (num, text) => `https://wa.me/${String(num).replace(/[^0-9]/g, '')}${text ? `?text=${encodeURIComponent(text)}` : ''}`;

// ── Dashboard ────────────────────────────────────────────────────────────────
function Tile({ label, value, sub, tone = 'text-text-primary', onClick }) {
  return (
    <button onClick={onClick} disabled={!onClick}
      className={`text-left rounded-xl border border-border bg-bg-panel p-3 ${onClick ? 'hover:border-cyan' : 'cursor-default'}`}>
      <div className="text-[10px] font-bold text-text-muted uppercase tracking-wider">{label}</div>
      <div className={`text-lg font-extrabold mt-1 ${tone}`}>{value}</div>
      {sub && <div className="text-[11px] text-text-muted">{sub}</div>}
    </button>
  );
}

// Phase 7: what still needs your real-world input before switching on.
const LEVEL = { block: ['Must fix', 'red'], warn: ['Check', 'amber'], info: ['Info', 'gray'], ok: ['OK', 'green'] };
function Readiness({ goTo }) {
  const [r, setR] = useState(null);
  const [showOk, setShowOk] = useState(false);
  useEffect(() => { marketAPI.readiness().then(x => setR(x.readiness)).catch(err => toast.error(err.message)); }, []);
  if (!r) return null;
  const shown = r.checks.filter(c => showOk || c.level !== 'ok');
  return (
    <Card>
      <SectionHeader title={r.ready ? '✅ Launch readiness — nothing blocking' : `🚦 Launch readiness — ${r.blocking} to fix before opening`}
        action={<button className="text-[11px] text-cyan" onClick={() => setShowOk(v => !v)}>{showOk ? 'hide OK items' : 'show all'}</button>} />
      <p className="text-[11px] text-text-muted mb-2">Live checks of your settings and products. "Must fix" = buyers would hit a problem. Then run the dry run in docs/dwi-market-launch-checklist.md.</p>
      {shown.length === 0 ? <p className="text-xs text-green">Everything checked is ready.</p> : (
        <div className="space-y-1">
          {shown.map(c => (
            <div key={c.key} className="flex items-start justify-between gap-2 text-xs border-b border-border py-1.5">
              <div><Badge label={LEVEL[c.level][0]} color={LEVEL[c.level][1]} /> <span className="text-text-primary font-semibold">{c.label}</span>
                <div className="text-text-muted mt-0.5">{c.detail}</div></div>
              {c.fix && c.level !== 'ok' && <Button size="xs" variant="ghost" onClick={() => goTo(c.fix)}>Fix →</Button>}
            </div>
          ))}
        </div>
      )}
    </Card>
  );
}

export function DashboardSection({ goTo }) {
  const [d, setD] = useState(null);
  const load = useCallback(() => marketAPI.dashboard().then(r => setD(r.dashboard)).catch(err => toast.error(err.message)), []);
  useEffect(() => { load(); }, [load]);
  if (!d) return <Card><div className="py-10 flex justify-center"><Spinner /></div></Card>;
  const warn = n => (n > 0 ? 'text-amber' : 'text-text-primary');
  const sw = (on, label) => <Badge label={`${label}: ${on ? 'ON' : 'OFF'}`} color={on ? 'green' : 'gray'} />;
  return (
    <div className="space-y-4">
      <Card>
        <SectionHeader title="Market today" action={<Button size="xs" variant="secondary" onClick={load}>Refresh</Button>} />
        <div className="flex gap-2 flex-wrap mb-1">
          {sw(d.switches.shop, 'Shop')}{sw(d.switches.page, 'Public page')}{sw(d.switches.sellers, 'Seller sign-up')}{sw(d.switches.requests, 'Buy requests')}
          <button className="text-[11px] text-cyan" onClick={() => goTo('settings')}>change in Settings →</button>
        </div>
      </Card>

      {!(d.switches.shop && d.switches.page) && <Readiness goTo={goTo} />}

      <Card>
        <div className="text-xs font-bold text-text-muted uppercase tracking-wider mb-2">Orders to act on</div>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
          <Tile label="Paid — to dispatch" value={d.orders.toDispatch} tone={warn(d.orders.toDispatch)} onClick={() => goTo('orders')} />
          <Tile label="On the way" value={d.orders.onTheWay} onClick={() => goTo('orders')} />
          <Tile label="Delivered — to complete" value={d.orders.toComplete} onClick={() => goTo('orders')} />
          <Tile label="Awaiting payment" value={d.orders.awaitingPayment} onClick={() => goTo('orders')} />
        </div>
      </Card>

      <Card>
        <div className="text-xs font-bold text-text-muted uppercase tracking-wider mb-2">Sales (paid orders, incl. delivery)</div>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
          <Tile label="Today" value={ugx(d.sales.today.totalUGX)} sub={`${d.sales.today.orders} order(s)`} />
          <Tile label="Last 7 days" value={ugx(d.sales.week.totalUGX)} sub={`${d.sales.week.orders} order(s)`} />
          <Tile label="Last 30 days" value={ugx(d.sales.month.totalUGX)} sub={`${d.sales.month.orders} order(s)`} />
          <Tile label="Margin, 30 days" value={ugx(d.margin30.marginUGX)} tone={d.margin30.marginUGX < 0 ? 'text-red' : 'text-green'}
            sub={d.margin30.marginPct != null ? `${d.margin30.marginPct}% · net after trips ${ugx(d.margin30.netUGX)}` : 'no costed sales yet'} onClick={() => goTo('report')} />
        </div>
      </Card>

      <div className="grid lg:grid-cols-2 gap-4">
        <Card>
          <div className="text-xs font-bold text-text-muted uppercase tracking-wider mb-2">Demand (buyers)</div>
          <div className="grid grid-cols-2 gap-2">
            <Tile label="New requests" value={d.demand.new} tone={warn(d.demand.new)} onClick={() => goTo('demand')} />
            <Tile label="Sourcing" value={d.demand.sourcing} onClick={() => goTo('demand')} />
            <Tile label="Quoted — waiting" value={d.demand.quoted} onClick={() => goTo('demand')} />
            <Tile label="Quotes expiring in 24h" value={d.demand.quotesExpiring24h} tone={warn(d.demand.quotesExpiring24h)} onClick={() => goTo('demand')} />
          </div>
        </Card>
        <Card>
          <div className="text-xs font-bold text-text-muted uppercase tracking-wider mb-2">Supply (sellers)</div>
          <div className="grid grid-cols-2 gap-2">
            <Tile label="Sellers to vet" value={d.supply.sellersPending} tone={warn(d.supply.sellersPending)} onClick={() => goTo('sellers')} />
            <Tile label="Listings to review" value={d.supply.listingsToReview} tone={warn(d.supply.listingsToReview)} onClick={() => goTo('supply')} />
            <Tile label="Updated by sellers" value={d.supply.listingsUpdated} tone={warn(d.supply.listingsUpdated)} onClick={() => goTo('supply')} />
            <Tile label="Approved, not in shop" value={d.supply.approvedNotPublished} onClick={() => goTo('supply')} />
          </div>
        </Card>
      </div>

      <div className="grid lg:grid-cols-3 gap-4">
        <Tile label="Messages waiting for DWI" value={d.messages.unread} tone={warn(d.messages.unread)} onClick={() => goTo('messages')} />
        <Tile label="Owed to sellers" value={ugx(d.payables.dueUGX)} tone={d.payables.count ? 'text-amber' : 'text-text-primary'}
          sub={`${d.payables.count} item(s), ${d.payables.sellers} seller(s)${d.payables.missingAmount ? ` · ${d.payables.missingAmount} need an amount` : ''}`} onClick={() => goTo('payables')} />
        <Tile label="Out of stock (in shop)" value={d.stock.outOfStock.length} tone={warn(d.stock.outOfStock.length)}
          sub={d.stock.outOfStock.slice(0, 4).join(', ') || 'none'} onClick={() => goTo('products')} />
      </div>
    </div>
  );
}

// ── Seller payables (records only) ───────────────────────────────────────────
const METHOD_LABEL = { mobile_money: 'Mobile Money', cash: 'Cash', bank: 'Bank', other: 'Other' };
const PAY_COLOR = { due: 'amber', paid: 'green', cancelled: 'gray' };

export function PayablesSection() {
  const [status, setStatus] = useState('due');
  const [sellerId, setSellerId] = useState('');
  const [data, setData] = useState(null);
  const [paying, setPaying] = useState(null);     // { seller, items[] }
  const [adding, setAdding] = useState(false);
  const [sellers, setSellers] = useState([]);

  const load = useCallback(async () => {
    try { setData(await marketAPI.payables({ status: status || undefined, sellerId: sellerId || undefined })); }
    catch (err) { toast.error(err.message); }
  }, [status, sellerId]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { marketAPI.adminSellers({ status: 'approved' }).then(r => setSellers(r.sellers || [])).catch(() => {}); }, []);

  const setAmount = async p => {
    const v = window.prompt(`Amount owed for "${p.description}" (UGX)`, p.amountUGX ?? '');
    if (v === null) return;
    try { await marketAPI.setPayableAmount(p._id, Number(String(v).replace(/[^0-9]/g, ''))); toast.success('Amount saved'); load(); }
    catch (err) { toast.error(err.message); }
  };
  const cancel = async p => {
    const why = window.prompt(`Cancel "${p.description}"? Say why (e.g. goods returned):`);
    if (!why) return;
    try { await marketAPI.cancelPayable(p._id, why); toast.success('Cancelled'); load(); }
    catch (err) { toast.error(err.message); }
  };
  const payAllFor = o => {
    const items = (data?.payables || []).filter(p => p.status === 'due' && String(p.sellerId) === String(o.sellerId));
    if (!items.length) { setStatus('due'); setSellerId(String(o.sellerId)); return toast('Showing that seller — press "Pay all due" again'); }
    setPaying({ seller: o.seller, items });
  };

  return (
    <div className="space-y-4">
      <Card>
        <SectionHeader title={`Owed to sellers — ${ugx(data?.totalDueUGX)}`} action={<Button size="sm" onClick={() => setAdding(true)}>+ Add a payable</Button>} />
        <p className="text-[11px] text-text-muted mb-3">
          Records only — the app never pays anyone. When an order is <b>delivered</b>, each line supplied by a known seller is added here
          (quantity × the cost recorded at order time). Pay the seller yourself (MoMo, cash, bank), then mark it paid with the reference.
          Use "+ Add a payable" for anything else you owe a seller, e.g. stock bought upfront.
        </p>
        {!data ? <div className="py-6 flex justify-center"><Spinner /></div>
          : data.owed.length === 0 ? <p className="text-xs text-text-muted">Nothing owed right now.</p>
          : (
            <Table headers={['Seller', 'Owed', 'Items', 'Oldest', '']}>
              {data.owed.map(o => (
                <tr key={String(o.sellerId)}>
                  <Td>
                    <div className="text-text-primary font-semibold">{o.seller.businessName || '—'}</div>
                    <div className="flex gap-2 text-xs">
                      {o.seller.phone && <a className="text-cyan" href={`tel:${o.seller.phone.replace(/\s/g, '')}`}>📞 {o.seller.phone}</a>}
                      {o.seller.whatsapp && <a className="text-green" target="_blank" rel="noopener noreferrer" href={wa(o.seller.whatsapp, `Hello ${o.seller.businessName}, this is DwiSend about your payment of ${ugx(o.amountUGX)}. `)}>WhatsApp</a>}
                    </div>
                  </Td>
                  <Td className="text-text-primary font-bold">{ugx(o.amountUGX)}{o.missing ? <div className="text-[11px] text-amber font-normal">{o.missing} without an amount</div> : null}</Td>
                  <Td>{o.count}</Td>
                  <Td className="text-[11px] text-text-muted">{day(o.oldest)}</Td>
                  <Td>
                    <div className="flex gap-1">
                      <Button size="xs" variant="success" onClick={() => payAllFor(o)}>Pay all due</Button>
                      <Button size="xs" variant="ghost" onClick={() => { setSellerId(String(o.sellerId)); setStatus(''); }}>History</Button>
                    </div>
                  </Td>
                </tr>
              ))}
            </Table>
          )}
      </Card>

      <Card>
        <SectionHeader title="Payables" action={
          <div className="flex gap-2">
            <Select value={sellerId} onChange={e => setSellerId(e.target.value)}>
              <option value="">All sellers</option>
              {sellers.map(s => <option key={s._id} value={s._id}>{s.businessName}</option>)}
            </Select>
            <Select value={status} onChange={e => setStatus(e.target.value)}>
              <option value="due">Due</option>
              <option value="paid">Paid</option>
              <option value="cancelled">Cancelled</option>
              <option value="">All</option>
            </Select>
          </div>
        } />
        {!data ? <div className="py-6 flex justify-center"><Spinner /></div>
          : data.payables.length === 0 ? <Empty icon="🧾" title="No payables here" />
          : (
            <Table headers={['For', 'Seller', 'Amount', 'Status', '']}>
              {data.payables.map(p => (
                <tr key={p._id} className="align-top">
                  <Td>
                    <div className="text-text-primary text-xs font-semibold">{p.description}</div>
                    <div className="text-[11px] text-text-muted">{p.source === 'order' ? `Order ${p.orderRef}` : 'Added by you'} · {day(p.createdAt)}</div>
                    {p.note && <div className="text-[11px] text-text-muted">{p.note}</div>}
                  </Td>
                  <Td className="text-xs">{p.seller?.businessName || '—'}</Td>
                  <Td className="whitespace-nowrap">
                    {p.amountUGX != null ? <span className="text-text-primary font-semibold">{ugx(p.amountUGX)}</span> : <span className="text-amber text-xs">amount needed</span>}
                    {p.costPerUnitUGX != null && <div className="text-[11px] text-text-muted">{p.qty} × {ugx(p.costPerUnitUGX)}</div>}
                  </Td>
                  <Td>
                    <Badge label={p.status} color={PAY_COLOR[p.status]} />
                    {p.status === 'paid' && <div className="text-[11px] text-text-muted mt-1">{METHOD_LABEL[p.method]}{p.reference ? ` · ${p.reference}` : ''} · {day(p.paidAt)}{p.paidBy?.name ? ` · ${p.paidBy.name}` : ''}</div>}
                    {p.status === 'cancelled' && <div className="text-[11px] text-text-muted mt-1">{p.cancelReason}</div>}
                  </Td>
                  <Td>
                    {p.status === 'due' && (
                      <div className="flex flex-col gap-1 min-w-[100px]">
                        {p.amountUGX != null && <Button size="xs" variant="success" onClick={() => setPaying({ seller: p.seller, items: [p] })}>Mark paid</Button>}
                        <Button size="xs" variant="secondary" onClick={() => setAmount(p)}>{p.amountUGX != null ? 'Edit amount' : 'Set amount'}</Button>
                        <Button size="xs" variant="ghost" onClick={() => cancel(p)}>Cancel</Button>
                      </div>
                    )}
                  </Td>
                </tr>
              ))}
            </Table>
          )}
      </Card>
      {paying && <PayModal {...paying} onClose={() => setPaying(null)} onDone={() => { setPaying(null); load(); }} />}
      {adding && <AddPayableModal sellers={sellers} onClose={() => setAdding(false)} onDone={() => { setAdding(false); load(); }} />}
    </div>
  );
}

function PayModal({ seller, items, onClose, onDone }) {
  const [method, setMethod] = useState('mobile_money');
  const [reference, setRef] = useState('');
  const [note, setNote]     = useState('');
  const [busy, setBusy]     = useState(false);
  const missing = items.filter(p => p.amountUGX == null);
  const total = items.reduce((s, p) => s + (p.amountUGX || 0), 0);
  const go = async () => {
    setBusy(true);
    try {
      const r = await marketAPI.payPayables({ ids: items.map(p => p._id), method, reference, note });
      toast.success(r.message);
      onDone();
    } catch (err) { toast.error(err.message); setBusy(false); }
  };
  return (
    <Modal open onClose={onClose} title={`Mark paid — ${seller?.businessName || 'seller'}`} size="sm">
      <div className="space-y-3">
        <p className="text-xs text-text-muted">Pay the seller yourself first. This only records it — no money moves in the app.</p>
        <div className="text-sm space-y-0.5">{items.map(p => <div key={p._id} className="flex justify-between gap-2"><span className="text-xs">{p.description}</span><span>{p.amountUGX != null ? ugx(p.amountUGX) : '—'}</span></div>)}</div>
        <div className="text-sm font-bold text-text-primary flex justify-between border-t border-border pt-1"><span>Total</span><span>{ugx(total)}</span></div>
        {missing.length > 0 && <p className="text-xs text-red">{missing.length} item(s) have no amount — set them in the list first.</p>}
        <Select label="Paid by" value={method} onChange={e => setMethod(e.target.value)}>
          {Object.entries(METHOD_LABEL).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
        </Select>
        <Input label={method === 'cash' ? 'Reference (optional)' : 'Reference (MoMo / bank transaction ID)'} value={reference} onChange={e => setRef(e.target.value)} />
        <Input label="Note (optional)" value={note} onChange={e => setNote(e.target.value)} placeholder="e.g. paid to his wife's number" />
        <div className="flex gap-2">
          <Button variant="secondary" className="flex-1" onClick={onClose}>Back</Button>
          <Button className="flex-1" variant="success" loading={busy} disabled={missing.length > 0} onClick={go}>Mark {ugx(total)} paid</Button>
        </div>
      </div>
    </Modal>
  );
}

function AddPayableModal({ sellers, onClose, onDone }) {
  const [f, setF] = useState({ sellerId: sellers[0]?._id || '', description: '', amountUGX: '', note: '' });
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setF(p => ({ ...p, [k]: v }));
  const go = async () => {
    setBusy(true);
    try { await marketAPI.addPayable({ ...f, amountUGX: Number(f.amountUGX) }); toast.success('Payable added'); onDone(); }
    catch (err) { toast.error(err.message); setBusy(false); }
  };
  return (
    <Modal open onClose={onClose} title="Add what DWI owes a seller" size="sm">
      <div className="space-y-3">
        {sellers.length === 0 ? <p className="text-xs text-text-muted">No approved sellers yet (Sellers tab).</p> : (
          <Select label="Seller" value={f.sellerId} onChange={e => set('sellerId', e.target.value)}>
            {sellers.map(s => <option key={s._id} value={s._id}>{s.businessName}</option>)}
          </Select>
        )}
        <Input label="What for" value={f.description} onChange={e => set('description', e.target.value)} placeholder="e.g. 40 bags beans bought for stock" />
        <Input label="Amount (UGX)" type="number" min="1" step="1" value={f.amountUGX} onChange={e => set('amountUGX', e.target.value)} />
        <Input label="Note (optional)" value={f.note} onChange={e => set('note', e.target.value)} />
        <div className="flex gap-2">
          <Button variant="secondary" className="flex-1" onClick={onClose}>Cancel</Button>
          <Button className="flex-1" loading={busy} disabled={!sellers.length} onClick={go}>Add</Button>
        </div>
      </div>
    </Modal>
  );
}

// ── Margin report ────────────────────────────────────────────────────────────
const GROUP_LABEL = { product: 'Product', category: 'Category', month: 'Month', order: 'Order' };

export function MarginSection() {
  const today = new Date();
  const [q, setQ] = useState({ from: isoDay(new Date(today.getTime() - 30 * 864e5)), to: isoDay(today), groupBy: 'product' });
  const [r, setR] = useState(null);
  const load = useCallback(() => marketAPI.marginReport(q).then(x => setR(x.report)).catch(err => toast.error(err.message)), [q]);
  useEffect(() => { load(); }, [load]);
  const set = (k, v) => setQ(p => ({ ...p, [k]: v }));
  const t = r?.totals;

  const csv = () => {
    const head = [GROUP_LABEL[r.groupBy], 'Orders', 'Revenue', 'Cost', 'Margin', 'Margin %', 'Revenue without cost', 'Delivery fees', 'Trip costs', 'Net'];
    const line = x => [x.label, x.orders, x.revenueUGX, x.costUGX, x.marginUGX, x.marginPct ?? '', x.unknownCostRevenueUGX, x.deliveryFeeUGX, x.tripCostUGX, x.netUGX];
    const rows = [head, ...r.rows.map(line), line({ ...t, label: 'TOTAL' })]
      .map(cols => cols.map(c => `"${String(c).replace(/"/g, '""')}"`).join(',')).join('\n');
    const a = document.createElement('a');
    a.href = URL.createObjectURL(new Blob([rows], { type: 'text/csv' }));
    a.download = `dwisend-margin-${q.from}-to-${q.to}-by-${r.groupBy}.csv`;
    a.click();
    URL.revokeObjectURL(a.href);
  };

  return (
    <Card>
      <SectionHeader title="Margin report" action={
        <div className="flex gap-2 items-end flex-wrap">
          <Input label="From" type="date" value={q.from} onChange={e => set('from', e.target.value)} />
          <Input label="To" type="date" value={q.to} onChange={e => set('to', e.target.value)} />
          <Select label="By" value={q.groupBy} onChange={e => set('groupBy', e.target.value)}>
            {Object.entries(GROUP_LABEL).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
          </Select>
          {r && <Button size="sm" variant="secondary" onClick={csv}>⬇ CSV</Button>}
        </div>
      } />
      <p className="text-[11px] text-text-muted mb-3">
        Paid orders placed in the period (paid, dispatched, delivered, completed). Margin = selling price − the cost recorded when the
        order was placed (seller price or product cost). Lines with no cost are counted as revenue but left out of the margin — add costs
        on products to fill the gap. Net = margin + delivery fees − trip costs. Mobile-money / wallet fees are not included.
      </p>
      {!r ? <div className="py-10 flex justify-center"><Spinner /></div> : (
        <>
          <div className="grid grid-cols-2 sm:grid-cols-5 gap-2 mb-4">
            <Tile label="Revenue (items)" value={ugx(t.revenueUGX)} sub={`${t.orders} order(s)`} />
            <Tile label="Cost" value={ugx(t.costUGX)} />
            <Tile label="Margin" value={ugx(t.marginUGX)} tone={t.marginUGX < 0 ? 'text-red' : 'text-green'} sub={t.marginPct != null ? `${t.marginPct}% of costed sales` : ''} />
            <Tile label="Delivery fees − trips" value={ugx(t.deliveryFeeUGX - t.tripCostUGX)} sub={`${ugx(t.deliveryFeeUGX)} in · ${ugx(t.tripCostUGX)} trips`} />
            <Tile label="Net" value={ugx(t.netUGX)} tone={t.netUGX < 0 ? 'text-red' : 'text-text-primary'} />
          </div>
          {t.unknownCostLines > 0 && <p className="text-xs text-amber mb-3">{t.unknownCostLines} line(s) worth {ugx(t.unknownCostRevenueUGX)} had no cost recorded, so their margin is unknown.</p>}
          {r.rows.length === 0 ? <Empty icon="📊" title="No paid orders in this period" /> : (
            <Table headers={[GROUP_LABEL[r.groupBy], 'Orders', 'Revenue', 'Cost', 'Margin', '%', ...(r.groupBy === 'order' || r.groupBy === 'month' ? ['Delivery − trips', 'Net'] : [])]}>
              {r.rows.map(x => (
                <tr key={x.key}>
                  <Td>
                    <div className="text-text-primary text-xs font-semibold">{x.label}</div>
                    {x.date && <div className="text-[11px] text-text-muted">{day(x.date)} · {x.buyerName}{x.quoteRef ? ` · ${x.quoteRef}` : ''}</div>}
                    {x.unknownCostLines > 0 && <div className="text-[11px] text-amber">{ugx(x.unknownCostRevenueUGX)} without cost</div>}
                  </Td>
                  <Td>{x.orders}</Td>
                  <Td>{ugx(x.revenueUGX)}</Td>
                  <Td>{ugx(x.costUGX)}</Td>
                  <Td className={x.marginUGX < 0 ? 'text-red' : 'text-green'}>{ugx(x.marginUGX)}</Td>
                  <Td>{x.marginPct != null ? `${x.marginPct}%` : '—'}</Td>
                  {(r.groupBy === 'order' || r.groupBy === 'month') && <><Td>{ugx(x.deliveryFeeUGX - x.tripCostUGX)}</Td><Td className="font-semibold text-text-primary">{ugx(x.netUGX)}</Td></>}
                </tr>
              ))}
            </Table>
          )}
        </>
      )}
    </Card>
  );
}
